import { Group, Flex } from "@mantine/core"
import { ChatAvatar } from "./ChatAvatar"
import { ChatName } from "./ChatName"
import { useDisclosure } from "@mantine/hooks"
import { DefaultError } from "../../../-components/error-context"
import { UpdateChatModal } from "./UpdateChatModal"
import { useGetAuthenticatedUser } from "@/routes/app/-components/use-authenticated-user"
import { DottedMenu } from "@components/dotted-menu"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import {
  getChatGetChatByIdQueryKey,
  getChatGetChatsQueryKey,
  getChatUserHasArchivedChatQueryKey,
  useChatDeleteChat,
  useChatGetChatByIdSuspense,
  useChatToggleInactivateChat,
  useChatToggleMuteChat,
} from "@/orval/gen"

interface Props {
  chatId: number
  rightSection?: React.ReactElement
}

export function ChatHeaderGroup({ chatId, rightSection }: Props) {
  const { data: chat } = useChatGetChatByIdSuspense({
    chatId,
  })

  if (!chat) throw new DefaultError({ title: "Something went wrong." })

  const [
    updateModalOoened,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useDisclosure(false)

  const currentUserId = useGetAuthenticatedUser().data.userId
  const currentUserMember = chat.chatMembers.find(
    (cm) => cm.userId === currentUserId
  )
  const userIsAdmin = currentUserMember?.isAdmin

  const canEdit = chat.chatType !== "Direct" && userIsAdmin
  const queryClient = useQueryClient()
  const { mutateAsync: muteChat } = useChatToggleMuteChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getChatGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getChatGetChatByIdQueryKey({ chatId: chat.chatId }),
        })
      },
    },
  })
  const navigate = useNavigate()
  const { mutateAsync: inactivateChat } = useChatToggleInactivateChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getChatGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getChatGetChatByIdQueryKey({ chatId: chat.chatId }),
        })
        queryClient.invalidateQueries({
          queryKey: getChatUserHasArchivedChatQueryKey(),
        })
        navigate({ to: "/app/com/chat" })
      },
    },
  })
  const { mutateAsync: deleteChat } = useChatDeleteChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getChatGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getChatUserHasArchivedChatQueryKey(),
        })
        navigate({ to: "/app/com/chat" })
      },
    },
  })
  const { t } = useTranslation("app_server")

  const menuItems = [
    {
      children: currentUserMember?.isMuted ? t("Unmute") : t("Mute"),
      onClick: async () => await muteChat({ params: { chatId: chat.chatId } }),
    },
    {
      children: currentUserMember?.isInactive ? t("Unarchive") : t("Archive"),
      onClick: async () =>
        await inactivateChat({ params: { chatId: chat.chatId } }),
    },
    ...(userIsAdmin && chat.isEmpty
      ? [
          {
            children: t("Delete"),
            onClick: async () =>
              await deleteChat({ params: { chatId: chat.chatId } }),
          },
        ]
      : []),
  ]

  return (
    <React.Fragment>
      <Group wrap="nowrap" justify="space-between" w={"100%"}>
        <Group
          w={"100%"}
          wrap="nowrap"
          {...(chat.chatType !== "Direct"
            ? {
                style: {
                  cursor: "pointer",
                },
                onClick: openUpdateModal,
              }
            : undefined)}
        >
          <ChatAvatar chat={chat} />
          <ChatName chat={chat} />
          {rightSection ? (
            <>
              <Flex flex="1" />
              {rightSection}
            </>
          ) : null}
        </Group>
        {!["Broadcast", "BroadcastFeed", "Feed"].includes(chat.chatType) && (
          <DottedMenu items={menuItems} />
        )}
      </Group>
      <UpdateChatModal
        opened={updateModalOoened}
        onClose={closeUpdateModal}
        chat={chat}
        readonly={!canEdit}
      />
    </React.Fragment>
  )
}
